import { createSyntheticMeta } from '@/contracts/questionnaire'
import {
  DEFAULT_SHARE_MAX_RESPONSES,
  type EmailProviderSetupContract,
  type EmailShareDraftContract,
  type EmailSharePreviewContract,
  type EmailSharePreviewInput,
  EmailSharePreviewInputSchema,
  type EmailShareRecipientContract,
  type EmailShareTemplateContract,
  type EmbedSnippetContract,
  type ShareSettingsContract,
  type TrackingLinkContract,
  type TrackingLinkInput,
  TrackingLinkInputSchema,
} from '@/contracts/share'
import {
  deleteEmailShareTemplateSnapshot,
  loadFixture,
  saveEmailShareTemplateSnapshot,
  saveShareSettingsSnapshot,
  saveTrackingLinkSnapshot,
} from '@/lib/mock-api/persistent-store'
import { buildCurrentOriginUrl, buildPublicFormUrl } from '@/lib/public-form-url'

function getBaseUrl(formId: string) {
  return buildPublicFormUrl(formId)
}

const shareMaxResponsesDefaultChangedAt = '2026-06-01'
const legacyShareMaxResponsesDefault = 1000

function normalizePersistedShareSettings(
  settings: ShareSettingsContract,
  publicUrl: string,
): ShareSettingsContract {
  if (
    settings.maxResponses === legacyShareMaxResponsesDefault &&
    settings.sourceMeta.source === 'synthetic' &&
    settings.sourceMeta.dataDate < shareMaxResponsesDefaultChangedAt
  ) {
    return saveShareSettingsSnapshot({
      ...settings,
      publicUrl,
      maxResponses: DEFAULT_SHARE_MAX_RESPONSES,
      qrCodeUrl: `${publicUrl}?qr=1`,
      sourceMeta: createSyntheticMeta(shareMaxResponsesDefaultChangedAt),
    })
  }

  return {
    ...settings,
    publicUrl,
    qrCodeUrl: `${publicUrl}?qr=1`,
    sourceMeta: createSyntheticMeta(),
  }
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function uniqueRecipients(
  emails: string[],
  role: EmailShareRecipientContract['role'],
): EmailShareRecipientContract[] {
  return Array.from(new Set(emails.map((email) => email.trim()).filter(Boolean))).map((email) => ({
    id: `${role}-${email.toLocaleLowerCase()}`,
    email,
    name: email,
    role,
    source: 'manual',
    isValid: isValidEmail(email),
  }))
}

function replaceVariables(value: string, variables: Record<string, string>) {
  return Object.entries(variables).reduce(
    (result, [key, replacement]) => result.replaceAll(`{{${key}}}`, replacement),
    value,
  )
}

export function getEmailProviderSetups(): EmailProviderSetupContract[] {
  const commonEnv: EmailProviderSetupContract['envVars'] = [
    {
      key: 'EMAIL_PROVIDER_MODE',
      description: 'mock 只做預覽；live 需完成 provider/security review 才可啟用。',
      example: 'mock',
      requiredFor: ['webhook', 'gmail_smtp'],
      secret: false,
      location: '.dev.vars' as const,
    },
    {
      key: 'EMAIL_SEND_PROVIDER',
      description: '寄送 provider；目前 UI 支援 webhook 或 gmail_smtp 規劃。',
      example: 'webhook',
      requiredFor: ['webhook', 'gmail_smtp'],
      secret: false,
      location: '.dev.vars' as const,
    },
    {
      key: 'EMAIL_SEND_ENABLED',
      description: '寄信 kill switch。未通過審查前必須維持 false。',
      example: 'false',
      requiredFor: ['webhook', 'gmail_smtp'],
      secret: false,
      location: 'Cloudflare Pages encrypted env' as const,
    },
    {
      key: 'EMAIL_FROM_ADDRESS',
      description: '寄件人 Email。',
      example: 'no-reply@example.com',
      requiredFor: ['webhook', 'gmail_smtp'],
      secret: false,
      location: 'Cloudflare Pages encrypted env' as const,
    },
    {
      key: 'EMAIL_FROM_NAME',
      description: '寄件人顯示名稱。',
      example: 'MiaoForm',
      requiredFor: ['webhook', 'gmail_smtp'],
      secret: false,
      location: 'Cloudflare Pages encrypted env' as const,
    },
    {
      key: 'EMAIL_REPLY_TO',
      description: '收件人回覆時使用的 Reply-To。',
      example: 'support@example.com',
      requiredFor: ['webhook', 'gmail_smtp'],
      secret: false,
      location: 'Cloudflare Pages encrypted env' as const,
    },
    {
      key: 'EMAIL_DAILY_LIMIT',
      description: '每日寄送上限，用於 rate limit 和誤寄防護。',
      example: '100',
      requiredFor: ['webhook', 'gmail_smtp'],
      secret: false,
      location: '.dev.vars' as const,
    },
  ]

  return [
    {
      provider: 'webhook',
      label: 'Webhook 寄送',
      status: 'provider_not_configured',
      recommendedUse: '建議用於 Cloudflare Pages Functions，由外部郵件服務或自動化流程負責寄信。',
      warning: '正式啟用前需要 HMAC secret、timeout、retry、audit log 與 kill switch。',
      envVars: [
        ...commonEnv,
        {
          key: 'EMAIL_WEBHOOK_URL',
          description: 'MiaoForm 將寄送 payload 的 HTTP endpoint。',
          example: 'https://your-mail-webhook.example.com/send',
          requiredFor: ['webhook'],
          secret: false,
          location: 'Cloudflare Pages encrypted env',
        },
        {
          key: 'EMAIL_WEBHOOK_SECRET',
          description: 'Webhook HMAC 簽章密鑰。',
          example: 'replace-with-secret',
          requiredFor: ['webhook'],
          secret: true,
          location: 'Cloudflare Pages encrypted env',
        },
      ],
      sourceMeta: createSyntheticMeta('2026-05-22'),
    },
    {
      provider: 'gmail_smtp',
      label: 'Gmail 應用程式密碼',
      status: 'provider_not_configured',
      recommendedUse: '僅適合低量測試或內部寄送；Cloudflare runtime 不建議直接使用 SMTP socket。',
      warning:
        '建議以 webhook wrapper 代理 Gmail SMTP，或後續改用 Gmail API / HTTP Email provider。',
      envVars: [
        ...commonEnv,
        {
          key: 'SMTP_HOST',
          description: 'Gmail SMTP host。',
          example: 'smtp.gmail.com',
          requiredFor: ['gmail_smtp'],
          secret: false,
          location: '.dev.vars',
        },
        {
          key: 'SMTP_PORT',
          description: 'Gmail SSL SMTP 通常使用 465；STARTTLS 可用 587。',
          example: '465',
          requiredFor: ['gmail_smtp'],
          secret: false,
          location: '.dev.vars',
        },
        {
          key: 'SMTP_SECURE',
          description: '465 使用 true；587 使用 false 並由 provider 啟用 STARTTLS。',
          example: 'true',
          requiredFor: ['gmail_smtp'],
          secret: false,
          location: '.dev.vars',
        },
        {
          key: 'SMTP_USER',
          description: 'Gmail 帳號。',
          example: 'your@gmail.com',
          requiredFor: ['gmail_smtp'],
          secret: false,
          location: 'Cloudflare Pages encrypted env',
        },
        {
          key: 'SMTP_APP_PASSWORD',
          description: 'Gmail 兩步驟驗證產生的應用程式密碼。',
          example: 'replace-with-gmail-app-password',
          requiredFor: ['gmail_smtp'],
          secret: true,
          location: 'Cloudflare Pages encrypted env',
        },
      ],
      sourceMeta: createSyntheticMeta('2026-05-22'),
    },
  ]
}

export async function getMockShareSettings(formId: string): Promise<ShareSettingsContract> {
  const state = loadFixture()
  const form = state.forms.find((item) => item.id === formId) ?? state.forms[0]
  const persistedSettings = state.shareSettings[form.id]
  if (persistedSettings) {
    const publicUrl = getBaseUrl(form.id)
    return normalizePersistedShareSettings(persistedSettings, publicUrl)
  }

  const publicUrl = getBaseUrl(form.id)
  return saveShareSettingsSnapshot({
    formId: form.id,
    publicUrl,
    isEnabled: form.status === 'published',
    closeAt: typeof form.settings.closedAt === 'string' ? form.settings.closedAt : null,
    maxResponses:
      typeof form.settings.maxResponses === 'number'
        ? form.settings.maxResponses
        : DEFAULT_SHARE_MAX_RESPONSES,
    allowRepeat: form.settings.allowResubmit === true,
    defaultSalesCode: 'ARIEL',
    defaultCampaignCode: 'Q2-NURTURE',
    hiddenFields: {
      salesCode: 'ARIEL',
      campaignCode: 'Q2-NURTURE',
    },
    qrCodeUrl: `${publicUrl}?qr=1`,
    sourceMeta: createSyntheticMeta(shareMaxResponsesDefaultChangedAt),
  })
}

export async function updateMockShareSettings(
  formId: string,
  patch: Partial<Omit<ShareSettingsContract, 'formId' | 'publicUrl' | 'qrCodeUrl' | 'sourceMeta'>>,
): Promise<ShareSettingsContract> {
  const current = await getMockShareSettings(formId)
  return saveShareSettingsSnapshot({
    ...current,
    ...patch,
    sourceMeta: createSyntheticMeta(shareMaxResponsesDefaultChangedAt),
  })
}

export async function listMockEmbedSnippets(formId: string): Promise<{
  data: EmbedSnippetContract[]
  meta: ReturnType<typeof createSyntheticMeta>
}> {
  const publicUrl = getBaseUrl(formId)
  const embedScriptUrl = buildCurrentOriginUrl('/embed.js')
  return {
    data: [
      {
        mode: 'iframe',
        label: 'iframe',
        code: `<iframe src="${publicUrl}" width="100%" height="600" frameborder="0"></iframe>`,
        sourceMeta: createSyntheticMeta(),
      },
      {
        mode: 'popup',
        label: 'Popup',
        code: `<script src="${embedScriptUrl}"></script>
<script>
  MiaoForm.popup('${formId}', {
    trigger: 'button',
    buttonText: '填寫問卷',
    buttonColor: '#6366F1',
    delay: 3000
  })
</script>`,
        sourceMeta: createSyntheticMeta(),
      },
      {
        mode: 'slider',
        label: 'Slider',
        code: `MiaoForm.slider('${formId}', { position: 'right' })`,
        sourceMeta: createSyntheticMeta(),
      },
      {
        mode: 'popover',
        label: 'Popover',
        code: `MiaoForm.popover('${formId}', { position: 'bottom-right' })`,
        sourceMeta: createSyntheticMeta(),
      },
    ],
    meta: createSyntheticMeta(),
  }
}

export async function buildMockTrackingLink(
  input: TrackingLinkInput,
): Promise<TrackingLinkContract> {
  const parsed = TrackingLinkInputSchema.parse(input)
  const params = new URLSearchParams()
  if (parsed.utmSource) params.set('utm_source', parsed.utmSource)
  if (parsed.utmMedium) params.set('utm_medium', parsed.utmMedium)
  if (parsed.utmCampaign) params.set('utm_campaign', parsed.utmCampaign)
  if (parsed.salesCode) params.set('sales_code', parsed.salesCode)
  if (parsed.campaignCode) params.set('campaign_code', parsed.campaignCode)
  for (const [key, value] of Object.entries(parsed.hiddenFields)) {
    params.set(key, value)
  }
  const query = params.toString()
  const link = {
    id: `tracking-${parsed.formId}-${parsed.name.toLocaleLowerCase().replace(/\s+/g, '-')}`,
    formId: parsed.formId,
    name: parsed.name,
    url: `${getBaseUrl(parsed.formId)}${query ? `?${query}` : ''}`,
    utmSource: parsed.utmSource ?? '',
    utmMedium: parsed.utmMedium ?? '',
    utmCampaign: parsed.utmCampaign ?? '',
    salesCode: parsed.salesCode ?? '',
    campaignCode: parsed.campaignCode ?? '',
    hiddenFields: parsed.hiddenFields,
    sourceMeta: createSyntheticMeta(),
  }
  saveTrackingLinkSnapshot(link)
  return link
}

export async function listMockTrackingLinks(formId: string): Promise<{
  data: TrackingLinkContract[]
  meta: ReturnType<typeof createSyntheticMeta>
}> {
  return {
    data: loadFixture().trackingLinks[formId] ?? [],
    meta: createSyntheticMeta(),
  }
}

export async function listMockEmailShareTemplates(formId: string): Promise<{
  data: EmailShareTemplateContract[]
  meta: ReturnType<typeof createSyntheticMeta>
}> {
  const state = loadFixture()
  return {
    data: state.emailShareTemplates[formId] ?? [],
    meta: createSyntheticMeta(),
  }
}

export async function saveMockEmailShareTemplate(
  formId: string,
  template: EmailShareTemplateContract,
): Promise<EmailShareTemplateContract[]> {
  return saveEmailShareTemplateSnapshot(formId, {
    ...template,
    formId,
    updatedAt: new Date().toISOString(),
    sourceMeta: createSyntheticMeta(),
  })
}

export async function deleteMockEmailShareTemplate(
  formId: string,
  templateId: string,
): Promise<EmailShareTemplateContract[]> {
  return deleteEmailShareTemplateSnapshot(formId, templateId)
}

export async function listMockEmailShareRecipients(): Promise<{
  data: EmailShareRecipientContract[]
  meta: ReturnType<typeof createSyntheticMeta>
}> {
  const state = loadFixture()
  return {
    data: state.businessWorkflow.salesUsers.map((salesUser) => ({
      id: `sales-user-${salesUser.id}`,
      email: salesUser.email,
      name: salesUser.name,
      role: 'cc',
      source: 'sales_user',
      salesUserId: salesUser.id,
      isValid: isValidEmail(salesUser.email),
    })),
    meta: createSyntheticMeta(),
  }
}

export async function buildMockEmailSharePreview(
  input: EmailSharePreviewInput,
): Promise<EmailSharePreviewContract> {
  const parsed = EmailSharePreviewInputSchema.parse(input)
  const state = loadFixture()
  const form = state.forms.find((item) => item.id === parsed.formId) ?? state.forms[0]
  const firstSalesUser = state.businessWorkflow.salesUsers[0]
  const variables = {
    'form.title': form.title,
    'form.url': parsed.trackingUrl,
    'tracking.url': parsed.trackingUrl,
    'workspace.name': 'MiaoForm Internal Workspace',
    'sender.name': 'MiaoForm 管理員',
    'sender.email': 'admin@miao-form.case4.app',
    'sales.name': firstSalesUser?.name ?? '業務窗口',
    'sales.email': firstSalesUser?.email ?? 'sales@example.com',
    'campaign.code': state.shareSettings[form.id]?.defaultCampaignCode ?? 'CAMPAIGN',
    'recipient.email': parsed.to[0] ?? 'recipient@example.com',
    deadline: state.shareSettings[form.id]?.closeAt ?? '不限日期',
  }
  const to = uniqueRecipients(parsed.to, 'to')
  const cc = uniqueRecipients(parsed.cc, 'cc')
  const invalidRecipients = [...to, ...cc].filter((recipient) => !recipient.isValid)

  return {
    formId: parsed.formId,
    provider: parsed.provider,
    providerMode: 'mock',
    providerState: 'provider_not_configured',
    resolvedSubject: replaceVariables(parsed.subject, variables),
    resolvedBody: replaceVariables(parsed.body, variables),
    resolvedUrl: parsed.trackingUrl,
    to,
    cc,
    warnings: [
      '目前只產生 mock-safe 寄送預覽，不會寄出真實 Email。',
      '正式寄信需要 provider kill switch、rate limit、audit log 與 reviewer approval。',
      'Cloudflare Pages Functions 不建議直接使用 Gmail SMTP socket；Gmail app password 建議透過 webhook wrapper。',
      ...(invalidRecipients.length > 0
        ? [`${invalidRecipients.length} 個 Email 格式需要修正。`]
        : []),
    ],
    providerSetups: getEmailProviderSetups(),
    sourceMeta: createSyntheticMeta(),
  }
}

export function buildEmailShareDraftFromTemplate(
  formId: string,
  template: EmailShareTemplateContract,
  trackingUrl: string,
  cc: EmailShareRecipientContract[],
): EmailShareDraftContract {
  return {
    formId,
    templateId: template.id,
    to: [],
    cc,
    subject: template.subject,
    body: template.body,
    trackingUrl,
    provider: 'webhook',
    providerMode: 'mock',
  }
}
